import { useState } from "react";
import { Icon } from "../../../../components/icon/icon";
import { CustomQuestionModal } from "../../../../components/modal/app_modals/customQuestionModal";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import { useGetCustomQuestionsQuery } from "../../../../redux/features/event/event.api";
import {
  CreatedEvent,
  CustomQuestion,
} from "../../../../redux/features/event/event.types";
import { EventPermissions } from "../../../../utils/types";

const platformLabels: Record<string, string> = {
  instagram: "Instagram",
  x: "X",
  facebook: "Facebook",
  tiktok: "TikTok",
  snapchat: "Snapchat",
  linkedin: "LinkedIn",
  youtube: "YouTube",
  github: "GitHub",
  telegram: "Telegram",
  discord: "Discord",
};

const getQuestionMeta = (question: CustomQuestion) => {
  if (question.type === "options") {
    return `${question.optionType === "multiple" ? "Multiple selection" : "Single selection"}: ${
      question.options?.join(", ") ?? ""
    }`;
  }

  if (question.type === "social-profile") {
    return `Social Profile: ${
      platformLabels[question.platform ?? ""] ?? "Social"
    }`;
  }

  return "Text";
};

export const EventCustomQuestions = ({
  event,
  userEventPermissions,
}: {
  event: CreatedEvent;
  userEventPermissions: EventPermissions[];
}) => {
  const [selectedQuestion, setSelectedQuestion] = useState<
    CustomQuestion | undefined
  >();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const canManageQuestions = userEventPermissions.includes("editEventDetails");

  const {
    data: questionsResponse,
    isFetching,
    refetch,
  } = useGetCustomQuestionsQuery(
    { eventId: event._id },
    { skip: !event._id, refetchOnMountOrArgChange: true },
  );

  const questions = questionsResponse?.data ?? event.customQuestions ?? [];

  return (
    <>
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between gap-4">
          <p className="text-base font-bold text-grey-950">Custom Questions</p>

          <button
            type="button"
            disabled={!canManageQuestions}
            className="text-sm font-medium text-primary underline disabled:cursor-not-allowed disabled:text-grey-400"
            onClick={() => {
              setSelectedQuestion(undefined);
              handleModalOpen("customQuestionModal");
            }}
          >
            Add Question
          </button>
        </div>

        {isFetching ? (
          <div className="h-[168px] animate-pulse rounded-2xl bg-grey-50" />
        ) : questions.length === 0 ? (
          <button
            type="button"
            disabled={!canManageQuestions}
            className="flex w-full items-center gap-4 rounded-2xl border border-grey-100 px-4 py-3 text-left disabled:cursor-not-allowed"
            onClick={() => {
              setSelectedQuestion(undefined);
              handleModalOpen("customQuestionModal");
            }}
          >
            <div className="flex-1">
              <p className="text-base font-bold text-grey-900">
                Add a custom question
              </p>
              <p className="text-sm text-grey-700">
                We will ask guests the following questions when they register
                for the event.
              </p>
            </div>
            <Icon svg="plus" width={22} height={22} />
          </button>
        ) : (
          <div className="overflow-hidden rounded-2xl border border-grey-100">
            {questions.map((question) => (
              <div
                key={question._id}
                className="flex items-center gap-3 border-b border-grey-100 px-4 py-3 last:border-b-0"
              >
                <Icon
                  svg="grip"
                  width={18}
                  height={18}
                  className="shrink-0 text-grey-500"
                />

                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-bold text-grey-900 md:text-base">
                    {question.question}
                    {question.required ? " *" : ""}
                  </p>
                  <p className="truncate text-sm text-grey-700">
                    {getQuestionMeta(question)}
                  </p>
                </div>

                <button
                  type="button"
                  aria-label={`Edit ${question.question}`}
                  disabled={!canManageQuestions}
                  className="shrink-0 text-grey-950 disabled:cursor-not-allowed disabled:text-grey-400"
                  onClick={() => {
                    setSelectedQuestion(question);
                    handleModalOpen("customQuestionModal");
                  }}
                >
                  <Icon svg="pencil" width={20} height={20} />
                </button>
              </div>
            ))}
          </div>
        )}

        {questions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-grey-700">
            <Icon svg="info" width={16} height={16} />
            <span>Guests answer these when they register for the event.</span>
          </div>
        )}
      </div>

      <CustomQuestionModal
        open={modal === "customQuestionModal" && modalOpen}
        handleClose={() => {
          handleModalClose();
          setSelectedQuestion(undefined);
        }}
        event={event}
        question={selectedQuestion}
        onSuccess={() => {
          refetch();
        }}
      />
    </>
  );
};
